import { useState } from 'react';
import { ChevronDown, ChevronUp, ThumbsUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDistanceToNow } from 'date-fns';
import type { FeedbackItem, FeedbackComment } from '@/types/feedback';

interface FeedbackSuggestionListProps {
    items: FeedbackItem[];
    userVotes: Set<string>;
    onVote: (id: string) => void;
    onAddComment: (id: string, content: string, authorName: string) => Promise<boolean>;
    onFetchComments: (id: string) => void;
    comments: FeedbackComment[];
    commentsLoading: boolean;
    athleteName: string;
}

export function FeedbackSuggestionList({
    items,
    userVotes,
    onVote,
    onAddComment,
    onFetchComments,
    comments,
    commentsLoading,
    athleteName,
}: FeedbackSuggestionListProps) {
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [commentText, setCommentText] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);

    // Filter comments for the currently expanded item
    const activeComments = expandedId
        ? comments.filter(c => c.feedback_id === expandedId)
        : [];

    const handleExpand = (id: string) => {
        if (expandedId === id) {
            setExpandedId(null);
        } else {
            setExpandedId(id);
            onFetchComments(id);
        }
    };

    const handleCommentSubmit = async (item: FeedbackItem) => {
        if (!commentText.trim()) return;
        setSubmittingComment(true);
        const success = await onAddComment(item.id, commentText, athleteName);
        setSubmittingComment(false);
        if (success) setCommentText('');
    };

    if (!items.length) return null;

    return (
        <div className="space-y-3 p-3 bg-amber-50/50 border border-amber-200 rounded-lg animate-in fade-in slide-in-from-top-2">
            <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-amber-700 uppercase tracking-wider">
                    Possible Duplicates Found
                </p>
                <span className="text-[10px] text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
                    {items.length} match{items.length !== 1 ? 'es' : ''}
                </span>
            </div>

            <div className="divide-y divide-amber-100/50">
                {items.map((item) => {
                    const isExpanded = expandedId === item.id;
                    const hasVoted = userVotes.has(item.id);

                    return (
                        <div key={item.id} className="py-2 first:pt-0 last:pb-0">
                            <div className="flex items-start gap-3 group">
                                {/* Vote Button */}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className={`h-auto py-1 px-1.5 flex flex-col items-center gap-0.5 min-w-[32px] rounded border ${hasVoted
                                            ? 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100'
                                            : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                                        }`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onVote(item.id);
                                    }}
                                >
                                    <ThumbsUp className={`w-3 h-3 ${hasVoted ? 'fill-current' : ''}`} />
                                    <span className="text-[10px] font-bold">{item.vote_count}</span>
                                </Button>

                                {/* Content */}
                                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleExpand(item.id)}>
                                    <div className="flex items-center justify-between gap-2">
                                        <h4 className="text-sm font-medium text-slate-900 group-hover:text-blue-600 transition-colors truncate">
                                            {item.title}
                                        </h4>
                                        <Button variant="ghost" size="icon" className="h-4 w-4 text-slate-400">
                                            {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                        </Button>
                                    </div>

                                    <div className="flex items-center gap-2 mt-1">
                                        <span
                                            className={`text-[9px] px-1.5 py-0 rounded-sm font-bold uppercase tracking-wider ${item.status === 'shipped'
                                                    ? 'bg-green-100 text-green-700'
                                                    : item.status === 'in_progress'
                                                        ? 'bg-blue-100 text-blue-700'
                                                        : 'bg-slate-100 text-slate-600'
                                                }`}
                                        >
                                            {item.status.replace('_', ' ')}
                                        </span>
                                        <span className="text-[10px] text-slate-400">
                                            {item.comment_count} comments • {formatDistanceToNow(new Date(item.created_at))} ago
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Expanded Details */}
                            {isExpanded && (
                                <div className="mt-3 pl-11 pr-2 space-y-3">
                                    <p className="text-xs text-slate-600 leading-relaxed bg-white p-2 rounded border border-slate-100">
                                        {item.description}
                                    </p>

                                    <div className="space-y-3 border-t border-slate-100 pt-3">
                                        {/* Comments List */}
                                        {commentsLoading ? (
                                            <p className="text-xs text-slate-400 italic">Loading comments...</p>
                                        ) : activeComments.length > 0 ? (
                                            <div className="space-y-2 max-h-[150px] overflow-y-auto pr-1 custom-scrollbar">
                                                {activeComments.map((comment) => (
                                                    <div key={comment.id} className="text-xs">
                                                        <div className="flex justify-between items-baseline mb-0.5">
                                                            <span className={`font-bold ${comment.is_admin_reply ? 'text-blue-600' : 'text-slate-700'}`}>
                                                                {comment.author_display_name}
                                                                {comment.is_admin_reply && ' (Team)'}
                                                            </span>
                                                            <span className="text-[9px] text-slate-400">
                                                                {formatDistanceToNow(new Date(comment.created_at))} ago
                                                            </span>
                                                        </div>
                                                        <p className="text-slate-600 pl-2 border-l-2 border-slate-100">
                                                            {comment.content}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-slate-400 italic">No comments yet. Be the first!</p>
                                        )}

                                        {/* Add Comment */}
                                        <div className="flex gap-2 items-center">
                                            <Input
                                                className="h-8 text-xs bg-white"
                                                placeholder="Add your thoughts..."
                                                value={commentText}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCommentText(e.target.value)}
                                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleCommentSubmit(item);
                                                    }
                                                }}
                                            />
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                className="h-8 px-3 text-xs"
                                                disabled={!commentText.trim() || submittingComment}
                                                onClick={() => handleCommentSubmit(item)}
                                            >
                                                Post
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
